/**
 * The data model. See docs/11-data-model.md.
 *
 * The single most important rule in this file is that a DayEntry is the ONLY
 * thing stored. Cycles, phases, statistics and predictions are derived at read
 * time and never persisted (FR-001). A user correcting a period start date
 * three months late is normal and frequent; because cycles are derived, that
 * correction changes one field and everything downstream follows.
 */

/**
 * A LOCAL calendar date, 'YYYY-MM-DD'.
 *
 * Never a timestamp, never UTC-converted. NFR-08 is a correctness requirement:
 * a user who flies from Istanbul to Toronto must not see yesterday's period
 * move to the day before. See @domain/date for why `toISOString()` is banned.
 */
export type ISODate = string

/** Epoch milliseconds. Orders writes. NEVER determines which day something belongs to. */
export type Timestamp = number

export type DeviceId = string
export type SymptomId = string
export type MoodId = string
export type MedicationId = string
export type CustomFieldId = string

/**
 * Flow level.
 *
 * `spotting` is recorded, shown and searchable, but it is NOT a bleeding day
 * and does not open a cycle (FR-203). See @domain/cycles.
 */
export type FlowLevel = 'none' | 'spotting' | 'light' | 'medium' | 'heavy'

export const FLOW_LEVELS: readonly FlowLevel[] = ['none', 'spotting', 'light', 'medium', 'heavy']

/** Flow levels that count as bleeding for cycle derivation. Excludes spotting. */
export const BLEEDING_LEVELS: readonly FlowLevel[] = ['light', 'medium', 'heavy']

export type EnergyLevel = 'low' | 'medium' | 'high'

export interface SleepRecord {
  hours?: number
  quality?: 1 | 2 | 3 | 4 | 5
}

export interface MedicationRecord {
  id: MedicationId
  taken: boolean
  note?: string
}

export interface EntryMeta {
  createdAt: Timestamp
  updatedAt: Timestamp
  updatedBy: DeviceId
  /** Per-field updatedAt. Only populated when sync is enabled (data model Section 6). */
  fieldTimes?: Record<string, Timestamp>
}

/**
 * Everything recorded for one calendar day.
 *
 * Note what the absence of this record means, versus the presence of one with
 * no fields set (FR-004):
 *
 *   - no record        -> never logged. Unknown.
 *   - record, no fields -> the user logged, and had nothing to record.
 *
 * The difference is load-bearing. A gap in the data is only meaningful if
 * "nothing happened" is distinguishable from "nobody looked".
 */
export interface DayEntry {
  /** Schema version of THIS record. Enables lazy migration (FR-003). */
  v: number
  date: ISODate

  flow?: FlowLevel
  symptoms?: SymptomId[]
  /** Integer 0-10. Not a float — ranges people cannot distinguish create false precision. */
  pain?: number
  mood?: MoodId
  energy?: EnergyLevel
  sleep?: SleepRecord
  water?: number
  meds?: MedicationRecord[]
  note?: string
  tags?: string[]
  custom?: Record<CustomFieldId, string | number | boolean>

  meta: EntryMeta
}

export const ENTRY_SCHEMA_VERSION = 1

/** A maximal run of bleeding days, tolerating internal gaps of at most `maxGapDays`. */
export interface Period {
  start: ISODate
  end: ISODate
  /** Inclusive day count from start to end. */
  length: number
}

/** From one period's first day to the day before the next period's first day. */
export interface Cycle {
  start: ISODate
  end: ISODate
  /** Days from this period's start to the next period's start. */
  length: number
  periodLength: number
  /**
   * False when the length falls outside [MIN_CYCLE, MAX_CYCLE].
   *
   * Invalid cycles are excluded from prediction statistics but are shown
   * NEUTRALLY in the UI (FR-411). They are never labelled abnormal, irregular
   * or concerning — that is a step toward a clinical conclusion, and it is
   * distressing to someone who already knows.
   */
  valid: boolean
}

export interface DerivedCycles {
  periods: Period[]
  cycles: Cycle[]
  /** Set when the most recent period has no recorded end yet. */
  currentPeriod?: Period
  /** The in-progress cycle. Never completed, never counted in statistics. */
  currentCycle?: { start: ISODate; dayOfCycle: number }
}
